import {SearchResultItem} from './SearchResultItem.jsx';
import {ProductSuggestions} from './ProductSuggestions.jsx';
import {RecentSearches} from './RecentSearches.jsx';
import {PopularSearches} from './PopularSearches.jsx';
import React, {lazy, Suspense} from 'react';
import {Typography} from '@bonprix/design-system';
import useSearchTexts from '../hooks/use-search-texts.js';
import URL from '@core/scripts/helper/url.js';
import {SEARCH_LISTBOX_ID} from '../navigation/constants.js';

// Reuse the exact "unsere Lieblingsteile für dich" carousel from the no-results page.
// Lazy-loaded so the recommendation/product-card/carousel bundle is only fetched once the
// empty overlay is shown, keeping the always-present header bundle lean.
const RecommendationContainer = lazy(() => import('@article-overview/components/footer/RecommendationContainer.jsx'));
const RECO_TRACK_FOCUS_ROOM = '[&_ul]:p-1 [&_ul]:-mx-1 [&_ul]:scroll-p-1';
const RECO_CARD_RING =
    '[&_.product-card:focus-visible]:rounded [&_.product-card:focus-visible]:outline ' +
    '[&_.product-card:focus-visible]:outline-2 [&_.product-card:focus-visible]:outline-offset-2 ' +
    '[&_.product-card:focus-visible]:outline-clr-on-bg-high';
// toAbsolute prepends the locale base (e.g. /de_at/) — the endpoint 500s without it.
const recommendationAjaxData = () =>
    JSON.stringify({dataUrl: URL.toAbsolute('/ajax/recommendation/search/'), paramString: ''});

export const SearchDropdown = ({
    listMode,
    currentList,
    productSuggestions,
    deferredSearchTerm,
    selectedIndex,
    selectionMode,
    handleSelectItem,
    handleSearchResultItemHover,
    handleViewAllResults,
    handleProductSuggestionClick,
    handleInsertTerm,
    onRecommendationPointerDown,
    unselectField,
    clearSearchHistory,
    showPopular,
    popularSearches,
    popularHeader,
    showMoreLabel,
    showLessLabel,
}) => {
    const {data: searchTextsData} = useSearchTexts();
    const HISTORY_LIST_MODE = 'HISTORY';
    const SUGGESTIONS_LIST_MODE = 'SUGGESTIONS';

    const mobileTopGap = listMode === HISTORY_LIST_MODE ? '!pt-6' : '!pt-2';

    return (
        <div
            className={`search__input-dropdown ${mobileTopGap} xl:!pt-0 xl:!mt-6`}
            onMouseLeave={unselectField}
            onMouseDown={(e) => {
                e.preventDefault();
            }}
        >
            <div className={`search__input-dropdown-content px-4 xl:px-0${listMode === HISTORY_LIST_MODE ? ' xl:flex-auto xl:min-w-0' : ''}`}>
            {listMode === HISTORY_LIST_MODE ? (
                <>
                    {showPopular ? (
                        <PopularSearches
                            popularSearches={popularSearches}
                            header={popularHeader}
                            showMoreLabel={showMoreLabel}
                            showLessLabel={showLessLabel}
                            onSelectTerm={handleSelectItem}
                        />
                    ) : (
                        <RecentSearches
                            recentSearches={currentList}
                            historyHeader={searchTextsData?.searchHistoryHeader}
                            clearLabel={searchTextsData?.clearSearchHistory}
                            showMoreLabel={searchTextsData?.showMoreRecentSearches}
                            showLessLabel={searchTextsData?.showLessRecentSearches}
                            insertLabel={searchTextsData?.insertSearchTerm}
                            onSelectTerm={handleSelectItem}
                            onInsertTerm={handleInsertTerm}
                            clearSearchHistory={clearSearchHistory}
                        />
                    )}
                    <div
                        className="mt-8 xl:mt-5"
                        onPointerDown={onRecommendationPointerDown}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        <div className="mb-2">
                            <Typography variant="h6" component="span" className="text-clr-on-bg-mid">
                                {searchTextsData?.recommendationHeader || 'Unsere Lieblingsteile für Dich'}
                            </Typography>
                        </div>
                        <Suspense fallback={<div></div>}>
                            <div className={`[&>div]:!pb-0 [&>div]:!mb-0 [&>div>div:first-child]:hidden ${RECO_TRACK_FOCUS_ROOM} ${RECO_CARD_RING}`}>
                                <RecommendationContainer
                                    ajaxSwiperData={recommendationAjaxData()}
                                    texts={searchTextsData}
                                    page={'search'}
                                />
                            </div>
                        </Suspense>
                    </div>
                </>
            ) : (
                <ul
                    className={'search__input__dropdown-list [&>li:first-child]:!mt-2 xl:[&>li:first-child]:!mt-3'}
                    role="listbox"
                    id={SEARCH_LISTBOX_ID}
                    aria-label={searchTextsData?.searchPlaceholder || 'Wonach suchst Du?'}
                onMouseLeave={unselectField}>
                    {currentList.map((item, index) => (
                        <SearchResultItem
                            key={index}
                            searchTerm={deferredSearchTerm}
                            value={item}
                            listMode={listMode}
                            isActive={index === selectedIndex}
                            selectionMode={selectionMode}
                            onSelect={handleSelectItem}
                            id={`search-option-${index}`}
                            onMouseOver={() => {
                                handleSearchResultItemHover(index);
                            }}
                        />
                    ))}
                </ul>
            )}
            </div>
            {listMode === SUGGESTIONS_LIST_MODE && (
                <ProductSuggestions
                    products={productSuggestions}
                    searchTerm={deferredSearchTerm}
                    allArticlesLabel={searchTextsData?.allArticlesLink}
                    onViewAll={handleViewAllResults}
                    onProductClick={handleProductSuggestionClick}
                />
            )}
        </div>
    );
};
